import { useQuery } from '@tanstack/react-query';
import { loadEventWithTemplateAndCollaborators } from 'api';
import { GET_EVENT } from 'constants/queryKeys';

export type EventDetails = {
    event: Bizly.Event;
    template: Bizly.EventTemplate;
    collaborators: BizlyAPI.EventCollaboratorOrPending[];
};

// Nested under [GET_EVENT, eventId] so every existing invalidation of the event refreshes it too.
export const eventDetailsQueryKey = (eventId: number) => [GET_EVENT, eventId, 'details'] as const;

export const fetchEventDetails = async (eventId: number): Promise<EventDetails> => {
    const details: EventDetails = await loadEventWithTemplateAndCollaborators(eventId);

    // The schedule editors expect at least one day to render. The placeholder has always been this
    // partial shape (it is saved back as-is), so it is cast rather than padded with null fields.
    if (details.event && !details.event.schedule.length) {
        details.event.schedule.push({ start: null, end: null, guestrooms: [], agenda: [] } as unknown as Bizly.Day);
    }

    return details;
};

export const eventDetailsQueryOptions = (eventId: number) => ({
    queryKey: eventDetailsQueryKey(eventId),
    queryFn: () => fetchEventDetails(eventId),
    enabled: eventId > 0,
    // Edits are written to this cache optimistically and saved on a debounce; a focus refetch
    // mid-edit would briefly show the server's older copy. Invalidations still refetch.
    refetchOnWindowFocus: false,
});

export const useEventDetailsQuery = (eventId: number) => useQuery(eventDetailsQueryOptions(eventId));
